import { inject } from "@angular/core";
import { MsalService } from "@azure/msal-angular";

/**
 * MSAL App Initializer
 *
 * `@azure/msal-browser` v5 refuses every redirect/token API until
 * `initialize()` has resolved — `handleRedirectPromise`, `loginRedirect`,
 * `acquireTokenSilent`, `ssoSilent` and `logoutRedirect` all throw
 * `BrowserAuthError: uninitialized_public_client_application`.
 *
 * Initializing from the root component's `ngOnInit` is too late: the router's
 * initial navigation — and therefore `AuthService`'s constructor, the route
 * guards and the HTTP interceptors — runs inside the same `bootstrapApplication`
 * call, before that async `initialize()` has settled. Wiring this through
 * `provideAppInitializer` makes Angular await MSAL before the app starts, so
 * every one of those call sites sees an initialized instance.
 *
 * Resolving the redirect here also means that where an account exists — a
 * redirect response, or a cached account from a previous session —
 * `instance.getActiveAccount()` already returns it by the time `AuthService`
 * seeds its signals. A genuinely signed-out visitor still gets `null`, which
 * is what sends them to `/login`.
 */
export function initializeMsal(): Promise<void> {
  const instance = inject(MsalService).instance;

  return instance
    .initialize()
    .then(() => instance.handleRedirectPromise())
    .then((response) => {
      if (response?.account) {
        instance.setActiveAccount(response.account);
        return;
      }

      // No redirect response — adopt a cached account so a returning user
      // is not bounced to /login on a hard refresh.
      if (!instance.getActiveAccount()) {
        const [cachedAccount] = instance.getAllAccounts();
        if (cachedAccount) {
          instance.setActiveAccount(cachedAccount);
        }
      }
    })
    .catch((error: unknown) => {
      // Never block bootstrap on an auth failure: a rejected promise here
      // leaves the user staring at a blank page instead of the login screen.
      // eslint-disable-next-line no-console
      console.error("MSAL initialization/redirect handling failed:", error);
    });
}
